import React from 'react';
import { commentService } from '../services/comments';

interface CommentDisplayProps {
  comment: string;
  timestamp?: string;
  author?: string;
  type?: 'set' | 'workout' | 'exercise';
  showTimestamp?: boolean;
  showAuthor?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * CommentDisplay - Component for displaying comments with proper formatting
 * Handles Russian text display and provides consistent styling
 * Requirements: 9.4, 12.1, 12.2
 */
export const CommentDisplay: React.FC<CommentDisplayProps> = ({
  comment,
  timestamp,
  author,
  type = 'set',
  showTimestamp = false,
  showAuthor = false,
  compact = false,
  className = ''
}) => {
  if (!comment || comment.trim().length === 0) {
    return null;
  }

  const formattedComment = commentService.formatComment(comment);
  const isRussian = commentService.isRussianText(formattedComment);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMinutes < 1) {
        return 'только что';
      } else if (diffMinutes < 60) {
        return `${diffMinutes} мин назад`;
      } else if (diffHours < 24) {
        return `${diffHours} ч назад`;
      } else if (diffDays < 7) {
        return `${diffDays} дн назад`;
      } else {
        return date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'short',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
    } catch {
      return '';
    }
  };

  // Get icon based on comment type
  const getTypeIcon = () => {
    switch (type) {
      case 'workout':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'exercise':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'set':
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
          </svg>
        );
    }
  };

  // Get background color based on type
  const getBackgroundClass = () => {
    switch (type) {
      case 'workout':
        return 'bg-blue-50 border-blue-200';
      case 'exercise':
        return 'bg-green-50 border-green-200';
      case 'set':
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (compact) {
    return (
      <div className={`inline-flex items-center text-sm text-gray-600 ${className}`}>
        {getTypeIcon()}
        <span className="ml-1 italic">"{formattedComment}"</span>
        {showTimestamp && timestamp && (
          <span className="ml-2 text-xs text-gray-400">
            {formatTimestamp(timestamp)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`comment-display border rounded-lg p-3 ${getBackgroundClass()} ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {getTypeIcon()}
          <span className="ml-2 text-sm font-medium text-gray-700">
            {type === 'workout' ? 'Комментарий к тренировке' : 
             type === 'exercise' ? 'Комментарий к упражнению' : 
             'Комментарий к подходу'}
          </span>
        </div>
        
        {/* Language indicator */}
        {isRussian && (
          <div className="flex items-center text-xs text-green-600">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            RU
          </div>
        )}
      </div>

      {/* Comment Text */}
      <div className="text-gray-800 leading-relaxed">
        {formattedComment}
      </div>

      {/* Footer */}
      {(showAuthor || showTimestamp) && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
          {showAuthor && author && (
            <div className="text-sm text-gray-600">
              {author}
            </div>
          )}
          
          {showTimestamp && timestamp && (
            <div className="text-xs text-gray-500">
              {formatTimestamp(timestamp)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * CommentList - Component for displaying a list of comments
 */
interface CommentListProps {
  comments: Array<{
    id: string;
    comment: string;
    timestamp?: string;
    author?: string;
    type?: 'set' | 'workout' | 'exercise';
  }>;
  showTimestamps?: boolean;
  showAuthors?: boolean;
  compact?: boolean;
  className?: string;
}

export const CommentList: React.FC<CommentListProps> = ({
  comments,
  showTimestamps = true,
  showAuthors = false,
  compact = false,
  className = ''
}) => {
  if (!comments || comments.length === 0) {
    return (
      <div className={`text-center py-4 text-gray-500 ${className}`}>
        <div className="text-2xl mb-2">💬</div>
        <p className="text-sm">Комментариев пока нет</p>
      </div>
    );
  }

  return (
    <div className={`comment-list space-y-3 ${className}`}>
      {comments.map((comment) => (
        <CommentDisplay
          key={comment.id}
          comment={comment.comment}
          timestamp={comment.timestamp}
          author={comment.author}
          type={comment.type}
          showTimestamp={showTimestamps}
          showAuthor={showAuthors}
          compact={compact}
        />
      ))}
    </div>
  );
};

export default CommentDisplay;